import { store } from "Store/mainStore";
import DATAMART_SERVICES from "Pages/Contracts/Services/DatamartServices";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ContractHubStatus = "active" | "expired" | "pending" | "terminated" | "Due renewal";

export interface ContractHubItem {
  id: string;
  account_name: string;
  /** 1-star account number rendered in the hub grid Account ID column. */
  account_1_star: string;
  account_type: string;
  agreement_type: string;
  market__c: string;
  valid_from: string;
  valid_to: string;
  status: ContractHubStatus;
}

// export interface ContractHubStats {
//   total: number;
//   active: number;
//   expired: number;
//   pending: number;
//   terminated: number;
//   due_renewal: number;
// }

export interface ContractHubListParams {
  searchInput?: string;
  /** Account type filter (e.g. 'all' | 'direct' | 'partner').
   *  Passed through to the real API; mock data ignores this field. */
  accountType?: string;
  limit?: number;
  offset?: number;
}

export interface ContractHubListResponse {
  status: boolean;
  data: ContractHubItem[];
  total_count: number;
  message?: string;
}

export interface ContractHubStatsResponse {
  status: boolean;
  data: [string, number][];
  message?: string;
}

/** A single banner counter returned by the summary API. */
export interface ContractHubSummaryItem {
  title: string;
  value: number;
  color?: string;
  status?: string;
}

// ─── Service Class ────────────────────────────────────────────────────────────

class ContractHubService {
  private getState() {
    const reduxStore = store.getState();
    const email = reduxStore.authSlice?.login?.mail || "";
    const userId = reduxStore.authSlice?.login?.id || "";
    return { userId, email };
  }

  /**
   * Map the UI account-type filter to the value expected by the agreement_list API.
   * Defaults to "Customer"; the "partner" option maps to "Partner".
   */
  private mapAccountType(accountType?: string): string {
    return accountType === "All customers" ? "All" : accountType || "";
  }

  /**
   * Fetch the contract hub list from the agreement_list API.
   */
  fetchContractHubList = async (
    params: ContractHubListParams,
  ): Promise<ContractHubListResponse> => {
    const { searchInput = "", accountType, limit = 10, offset = 0 } = params;

    // const body = {
    //   account_type: this.mapAccountType(accountType),
    //   limit,
    //   offset,
    //   properties: ["a.id as id",
    //     "a.name as account_name",
    //     "a.accountnumber as account_id",
    //     "type as account_type",
    //     "agreement_type",
    //     "market__c",
    //     "valid_from",
    //     "valid_to",
    //     "status"],
    //   getCount: true,
    //   searchParam: searchInput,
    // };

    const response: any = await DATAMART_SERVICES.genericDatamartAPI({
      method: "get",
      // url: "/v2/contractHub/agreement_list",
      url: `/v2/contract/frame-agreement?offset=${offset}&limit=${limit}&search=${encodeURIComponent(searchInput)}&field_allowed_values=false&account_type=${this.mapAccountType(accountType)}&isContractHub=true`
      // data: body,
    });

    return {
      status: !!response?.status,
      data: (response?.data?.contracts?.agreements as ContractHubItem[]) || [],
      total_count: response?.data?.contracts?.total_count || 0,
      message: response?.message,
    };
  };

  /**
   * Fetch summary stats for the contract hub chips from the agreementStats API.
   */
  fetchContractHubStats = async (
    params: ContractHubListParams = {},
  ): Promise<any> => {
    const { accountType } = params;

    const response: any = await DATAMART_SERVICES.genericDatamartAPI({
      method: "get",
      url: `/v2/contract/hub/agreementStats?account_type=${this.mapAccountType(accountType)}&isContractHub=true`,
    });

    return response;
  };

  /**
   * Fetch the summary counters (status + count) for the contract hub banner.
   */
  fetchContractHubSummary = async (
    params: ContractHubListParams = {},
  ): Promise<any> => {
    const { accountType } = params;

    const response: any = await DATAMART_SERVICES.genericDatamartAPI({
      method: "get",
      url: `/v2/contract/hub/summary?account_type=${this.mapAccountType(accountType)}&isContractHub=true`,
    });

    return response;
  };
}

export const contractHubService = new ContractHubService();
